import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAgentPermissions } from "../../context/AgentPermissionsContext";
import { usePilotTraining } from "../../context/PilotTrainingContext";
import {
  DEMO_FIXTURE_NOTE,
  DEMO_INTRO,
  DEMO_STEPS,
  REVIEW_ACTIONS,
  TRUST_STATES,
  trainingProgress,
} from "./pilotTrainingSteps";

/**
 * Pilot Technician Training surface (MF §14B, PILOT-TECHNICIAN-TRAINING-001).
 *
 * A non-blocking drawer: it explains the real pilot path, teaches the trust
 * states in the product's own words, and offers a read-only exercise. It is
 * closed by default, never traps focus, never blocks a route, and performs no
 * write of any kind — the only persisted effect is the technician's own
 * checklist progress, which grants nothing.
 *
 * Every step links to a real screen and a real Help Center topic, and both are
 * filtered by the same rule Layout uses for nav, so the training can never show
 * a technician a screen they cannot open or a topic they may not read.
 */
export default function PilotTrainingPanel() {
  const training = usePilotTraining();
  const { user } = useAuth() || {};
  const { can: canAgent } = useAgentPermissions() || {};

  // Escape closes, matching the panel's non-blocking intent.
  useEffect(() => {
    if (!training || !training.open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") training.closeTraining();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [training]);

  if (!training || !training.open) return null;

  const role = user?.role;
  const isAdmin = role === "admin" || role === "owner";
  const audience = { isAdmin, canAgent, role };
  const { steps, completedSteps, total, allStepsDone } = trainingProgress(training.progress, audience);

  return (
    <div dir="rtl" className="fixed inset-0 z-40 flex justify-start" data-testid="pilot-training-panel">
      <div
        className="absolute inset-0 bg-black/30"
        aria-hidden="true"
        onClick={training.closeTraining}
      />
      <aside
        role="dialog"
        aria-label="הדרכת טכנאי פיילוט"
        className="relative h-full w-full max-w-lg overflow-y-auto bg-white shadow-xl p-4 text-sm text-gray-800"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">הדרכת טכנאי פיילוט</h2>
            <p className="text-xs text-gray-600 mt-1">
              סקירה קצרה של מסלול העבודה האמיתי, צעד אחר צעד. אפשר לעצור ולחזור מתי שנוח.
            </p>
          </div>
          <button
            type="button"
            onClick={training.closeTraining}
            aria-label="סגור הדרכה"
            className="shrink-0 text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
          >
            ✕
          </button>
        </div>

        <p className="mb-4 text-xs" data-testid="pilot-training-progress">
          הושלמו {completedSteps} מתוך {total} צעדים
          {allStepsDone ? " — כל הצעדים שבהרשאה שלך הושלמו." : ""}
        </p>

        {total === 0 ? (
          <p className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
            אין צעדי הדרכה להציג עבור ההרשאות של החשבון הזה.
          </p>
        ) : (
          <ol className="space-y-2 mb-5">
            {steps.map((step) => {
              const done = training.isStepDone(step.id);
              return (
                <li
                  key={step.id}
                  data-testid={`pilot-training-step-${step.id}`}
                  className="rounded border border-gray-200 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-gray-900">
                      {step.title}
                      {done ? <span className="text-green-700 text-xs"> · הושלם</span> : null}
                    </p>
                    <button
                      type="button"
                      onClick={() => training.markStep(step.id, !done)}
                      aria-pressed={done}
                      aria-label={`סמן את הצעד "${step.title}"`}
                      className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                    >
                      {done ? "בטל סימון" : "סמן כהושלם"}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-700">{step.what}</p>
                  <p className="mt-1 text-xs text-gray-600">{step.check}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                    <Link to={step.screen} className="text-blue-600 hover:underline">
                      פתח: {step.screenLabel}
                    </Link>
                    <Link
                      to={`/help?topic=${encodeURIComponent(step.helpTopic)}`}
                      className="text-blue-600 hover:underline"
                    >
                      עזרה בנושא
                    </Link>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <section className="mb-5" data-testid="pilot-training-trust-states">
          <h3 className="font-semibold text-gray-900 mb-1">איך לדעת מתי להאמין למסך</h3>
          <p className="text-xs text-gray-600 mb-2">
            המונחים האלה הם אותם מונחים שמופיעים במערכת עצמה.
          </p>
          <dl className="space-y-2">
            {TRUST_STATES.map((t) => (
              <div key={t.id} data-testid={`pilot-training-trust-${t.id}`} className="text-xs">
                <dt className="font-medium text-gray-900">{t.label}</dt>
                <dd className="text-gray-700">{t.explain}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="mb-5" data-testid="pilot-training-reviewable">
          <h3 className="font-semibold text-gray-900 mb-1">ההמלצה ניתנת לבדיקה</h3>
          <p className="text-xs text-gray-600 mb-2">
            הצעה של ה-AI אינה הוראה. ארבע דרכים לגיטימיות לעצור או לשנות אותה:
          </p>
          <ul className="space-y-2">
            {REVIEW_ACTIONS.map((a) => (
              <li key={a.id} data-testid={`pilot-training-review-${a.id}`} className="text-xs">
                <span className="font-medium text-gray-900">{a.label}: </span>
                <span className="text-gray-700">{a.explain}</span>
              </li>
            ))}
          </ul>
        </section>

        <DemoExercise done={training.progress.demoDone} onDone={training.markDemoDone} />

        <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3">
          {training.status === "paused" || training.status === "dismissed" ? (
            <button
              type="button"
              onClick={training.resume}
              className="rounded border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              המשך הדרכה
            </button>
          ) : (
            <button
              type="button"
              onClick={training.pause}
              className="rounded border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              השהה הדרכה
            </button>
          )}
          <button
            type="button"
            onClick={training.dismiss}
            className="rounded border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            סגור ואל תציג שוב
          </button>
          <button
            type="button"
            onClick={training.restart}
            className="rounded border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            התחל מחדש
          </button>
          <Link to="/help" onClick={training.closeTraining} className="text-xs text-blue-600 hover:underline">
            מרכז העזרה
          </Link>
        </div>
      </aside>
    </div>
  );
}

/**
 * §14B's safe exercise: fixed fixtures, no fetch, no mutation. Answering is
 * local state only; the single persisted fact is that the technician worked
 * through it.
 */
function DemoExercise({ done, onDone }) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const frame = DEMO_STEPS[index];
  const chosen = answers[frame.id];
  const answered = chosen !== undefined;
  const isLast = index === DEMO_STEPS.length - 1;
  const allAnswered = DEMO_STEPS.every((s) => answers[s.id] !== undefined);

  return (
    <section data-testid="pilot-training-demo" className="rounded border border-blue-200 bg-blue-50/50 p-3">
      <h3 className="font-semibold text-gray-900 mb-1">תרגיל הדגמה</h3>
      <p className="text-xs text-gray-700 mb-1">{DEMO_INTRO}</p>
      <p className="text-[11px] text-gray-500 mb-2">{DEMO_FIXTURE_NOTE}</p>

      <div
        data-testid={`pilot-training-demo-frame-${frame.id}`}
        className="rounded border border-gray-200 bg-white p-2"
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-xs font-medium text-gray-900">{frame.title}</p>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
            {frame.chip.label}
          </span>
        </div>
        <dl className="mb-2 text-[11px] text-gray-600">
          {frame.fixture.map((row) => (
            <div key={row.k} className="flex gap-2">
              <dt className="shrink-0">{row.k}:</dt>
              <dd>{row.v}</dd>
            </div>
          ))}
        </dl>

        <p className="text-xs font-medium text-gray-800 mb-1">{frame.prompt}</p>
        <div className="space-y-1">
          {frame.options.map((opt, i) => {
            const picked = chosen === i;
            const correct = i === frame.correct;
            const cls = !answered
              ? "border-gray-300 hover:bg-gray-50"
              : correct
              ? "border-green-400 bg-green-50"
              : picked
              ? "border-red-400 bg-red-50"
              : "border-gray-200";
            return (
              <button
                key={opt}
                type="button"
                data-testid={`pilot-training-demo-option-${frame.id}-${i}`}
                onClick={() => setAnswers((prev) => ({ ...prev, [frame.id]: i }))}
                className={`block w-full rounded border px-2 py-1 text-right text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${cls}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {answered ? (
          <p className="mt-2 text-xs text-gray-700" data-testid={`pilot-training-demo-explain-${frame.id}`}>
            {frame.explain}
          </p>
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <button
          type="button"
          disabled={index === 0}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-40 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          הקודם
        </button>
        <span className="text-[11px] text-gray-500">
          {index + 1} / {DEMO_STEPS.length}
        </span>
        <button
          type="button"
          disabled={isLast}
          onClick={() => setIndex((i) => Math.min(DEMO_STEPS.length - 1, i + 1))}
          className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-40 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          הבא
        </button>
      </div>

      {allAnswered ? (
        <div className="mt-2">
          {done ? (
            <p className="text-xs text-green-700" data-testid="pilot-training-demo-done">
              התרגיל הושלם.
            </p>
          ) : (
            <button
              type="button"
              onClick={onDone}
              className="rounded border border-green-400 bg-white px-2 py-1 text-xs text-green-800 hover:bg-green-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              סיימתי את התרגיל
            </button>
          )}
        </div>
      ) : null}
    </section>
  );
}
